import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';

import CONSTANTS from 'src/store/constants';
import { Button, Div, Icon, Text } from '@pinpoint/design-kit';

const ARTBOARD_WIDTH = '290px';
const ARTBOARD_HEIGHT = '180px';

function Artboard({ onClick, onClose, children, isFocussed, canClose, index, number, move, ...rest }) {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: CONSTANTS.DND.ITEMTYPE.SLIDE,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY && hoverClientX < hoverMiddleX) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY && hoverClientX > hoverMiddleX) {
        return;
      }
      move(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    item: {
      type: CONSTANTS.DND.ITEMTYPE.SLIDE,
      index,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  drag(drop(ref));
  return (
    <div ref={ref}>
      <Div
        w={ARTBOARD_WIDTH}
        h={ARTBOARD_HEIGHT}
        bg="gray200"
        hoverBg="gray100"
        d="flex"
        justify="center"
        align="center"
        cursor="move"
        m={{ x: '2rem', y: '2rem' }}
        pos="relative"
        flexDir="column"
        border={isFocussed ? '2px solid' : ''}
        borderColor="brand100"
        onClick={() => {
          onClick();
        }}
        opacity={isDragging ? 0.2 : 1}
        {...rest}
      >
        <Div pos="absolute" top="-1.5rem" left="0rem">
          <Text textSize="caption" textColor={isFocussed ? 'brand400' : 'gray600'}>
            {number}
          </Text>
        </Div>
        <Button
          top="-1rem"
          right="-1rem"
          pos="absolute"
          bg="brand500"
          hoverBg="brand300"
          rounded="circle"
          shadow="4"
          opacity="0.5"
          w="2rem"
          h="2rem"
          p="1rem"
          disabled={!canClose}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
        >
          <Icon name="Cross" color="white" />
        </Button>
        {children}
      </Div>
    </div>
  );
}

Artboard.propTypes = {
  onClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isFocussed: PropTypes.bool.isRequired,
  canClose: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  move: PropTypes.func.isRequired,
};

/* eslint-disable react/prop-types */

function Canvas({ slides, segments, selectImageFile, addSlide, removeSlide, moveSlide, selectedSlide, onSelect }) {
  return (
    <Div d="flex" flexWrap="wrap" m={{ y: '1rem' }}>
      {slides.map(({ number, view, segmentIds, imageFile, imageUrl }, index) => (
        <Artboard
          onClick={() => {
            onSelect(index);
          }}
          index={index}
          number={number}
          move={(fromIndex, toIndex) => {
            moveSlide(fromIndex, toIndex);
          }}
          onClose={() => removeSlide(index)}
          isFocussed={index === selectedSlide}
          canClose={!(slides.length === 1)}
        >
          {view ? (
            <Text textSize="subheader" m="0.5rem">
              {CONSTANTS.VIEWS[view].name}
            </Text>
          ) : (
            <Text m="0.5rem" textColor="gray900">
              Select View
            </Text>
          )}
          {segmentIds
            .map((segmentId) => segments.find(({ id }) => id === segmentId))
            .filter((segment, index_) => segment && (view ? index_ < CONSTANTS.VIEWS[view].maxSegments : true))
            .map(({ filter, dataset, name }) => (
              <Text textSize="caption" m="0.5rem">
                {filter === 'POPULATION_SNAPSHOT' ? dataset.name : name}
              </Text>
            ))}
          {view && CONSTANTS.VIEWS[view].imageUpload ? (
            imageFile || imageUrl ? (
              <Text textSize="caption" m="0.5rem">
                {(imageFile && imageFile.name) ||
                  (imageUrl && imageUrl.substring(imageUrl.lastIndexOf('/') + 1)) ||
                  '<filename>'}
              </Text>
            ) : (
              <Button
                bg="brand500"
                hoverBg="brand300"
                rounded="circle"
                shadow="4"
                opacity="0.5"
                w="2.5rem"
                h="2.4rem"
                p="1rem"
                pos="relative"
                onClick={() => {}}
              >
                <Icon pos="absolute" name="Photo" color="white" />
                <input
                  type="file"
                  name="file"
                  onChange={(event) => {
                    selectImageFile(index, event.target.files[0]);
                  }}
                  style={{
                    opacity: '0',
                    width: '2.5rem',
                    height: '2.5rem',
                    cursor: 'pointer',
                  }}
                />
              </Button>
            )
          ) : null}
        </Artboard>
      ))}
      <Div d="flex" w={ARTBOARD_WIDTH} h={ARTBOARD_HEIGHT} m={{ r: '2rem', b: '2rem' }} align="center" justify="center">
        <Button
          bg="brand500"
          hoverBg="brand300"
          rounded="circle"
          shadow="4"
          opacity="0.5"
          w="2.5rem"
          h="2.5rem"
          p="1rem"
          onClick={() => {
            addSlide();
          }}
        >
          <Icon name="Plus" color="white" m={{ r: '.5em' }} />
        </Button>
      </Div>
    </Div>
  );
}

/* eslint-enable react/prop-types */

export default Canvas;
